import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const PrescriptionAnalytics = () => {
  const monthlyData = [
    { month: 'Jan', prescriptions: 145, fulfilled: 132 },
    { month: 'Feb', prescriptions: 158, fulfilled: 149 },
    { month: 'Mar', prescriptions: 172, fulfilled: 165 },
    { month: 'Apr', prescriptions: 189, fulfilled: 178 },
    { month: 'May', prescriptions: 203, fulfilled: 195 },
    { month: 'Jun', prescriptions: 218, fulfilled: 208 }
  ];

  const statusData = [
    { name: 'Active', value: 45, color: '#10B981' },
    { name: 'Fulfilled', value: 38, color: '#2563EB' },
    { name: 'Pending', value: 12, color: '#F59E0B' },
    { name: 'Cancelled', value: 5, color: '#DC2626' }
  ];

  const adherenceData = [
    { week: 'Week 1', adherence: 95 },
    { week: 'Week 2', adherence: 92 },
    { week: 'Week 3', adherence: 88 },
    { week: 'Week 4', adherence: 91 }
  ];

  const topMedications = [
    { name: 'Lisinopril', count: 28, trend: 'up' },
    { name: 'Metformin', count: 24, trend: 'up' },
    { name: 'Atorvastatin', count: 22, trend: 'down' },
    { name: 'Amlodipine', count: 19, trend: 'up' },
    { name: 'Omeprazole', count: 16, trend: 'stable' }
  ];

  const complianceMetrics = [
    { label: 'FHIR Compliance', value: 98, color: 'text-success' },
    { label: 'Blockchain Verification', value: 95, color: 'text-primary' },
    { label: 'Digital Signatures', value: 100, color: 'text-success' },
    { label: 'Pharmacy Integration', value: 92, color: 'text-warning' }
  ];

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return { icon: 'TrendingUp', color: 'text-success' };
      case 'down':
        return { icon: 'TrendingDown', color: 'text-error' };
      default:
        return { icon: 'Minus', color: 'text-muted-foreground' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Prescriptions</p>
              <p className="text-2xl font-semibold text-foreground">1,285</p>
              <p className="text-xs text-success flex items-center space-x-1 mt-1">
                <Icon name="TrendingUp" size={12} />
                <span>+12% from last month</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={24} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Prescriptions</p>
              <p className="text-2xl font-semibold text-foreground">456</p>
              <p className="text-xs text-success flex items-center space-x-1 mt-1">
                <Icon name="TrendingUp" size={12} />
                <span>+8% from last month</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={24} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Fulfillment Rate</p>
              <p className="text-2xl font-semibold text-foreground">94.2%</p>
              <p className="text-xs text-success flex items-center space-x-1 mt-1">
                <Icon name="TrendingUp" size={12} />
                <span>+2.1% from last month</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-warning" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg. Processing Time</p>
              <p className="text-2xl font-semibold text-foreground">2.4h</p>
              <p className="text-xs text-success flex items-center space-x-1 mt-1">
                <Icon name="TrendingDown" size={12} />
                <span>-15min from last month</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-accent" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Prescriptions Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Monthly Prescriptions</h3>
            <Icon name="BarChart3" size={20} className="text-muted-foreground" />
          </div>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="prescriptions" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="fulfilled" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Status Distribution</h3>
            <Icon name="PieChart" size={20} className="text-muted-foreground" />
          </div>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {statusData?.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm text-muted-foreground">{item?.name}: {item?.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Medications */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Top Medications</h3>
            <Icon name="Pill" size={20} className="text-muted-foreground" />
          </div>
          <div className="space-y-3">
            {topMedications?.map((med, index) => {
              const trendInfo = getTrendIcon(med?.trend);
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-foreground">{med?.name}</span>
                    <Icon name={trendInfo?.icon} size={14} className={trendInfo?.color} />
                  </div>
                  <span className="text-sm text-muted-foreground">{med?.count} prescriptions</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Compliance Metrics */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Compliance Metrics</h3>
            <Icon name="Shield" size={20} className="text-muted-foreground" />
          </div>
          <div className="space-y-4">
            {complianceMetrics?.map((metric, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{metric?.label}</span>
                  <span className={`text-sm font-medium ${metric?.color}`}>{metric?.value}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      metric?.value >= 95 ? 'bg-success' :
                      metric?.value >= 90 ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${metric?.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Patient Adherence Trend */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-foreground">Patient Adherence Trend</h3>
          <Icon name="Activity" size={20} className="text-muted-foreground" />
        </div>
        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={adherenceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} domain={[80, 100]} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }} 
              />
              <Line 
                type="monotone" 
                dataKey="adherence" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionAnalytics;